import { useState } from "react";
import { Lock, Sparkles } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { getEdgyCreditState, useQuery } from "wasp/client/operations";
import useTranslation from "../../client/hooks/useTranslation";
import { Button } from "../../client/components/ui/button";
import EdgyDrawer from "../../edgy/EdgyDrawer";
import { useEdgyAccess } from "../../edgy/useEdgyAccess";
import { PageBody, PageHeader } from "../shell/LiteLayout";
import { useLiteActions } from "../shell/LiteActions";
import { Segmented } from "../ui/ui";
import { AnalysesTab } from "./edgy/AnalysesTab";
import { PlaybookTab } from "./edgy/PlaybookTab";

type Tab = "analyses" | "playbook";

/**
 * Analyse EdgyAI du Lite : analyses passées et playbook réunis sur une page.
 * L'onglet vit dans ?vue= ; sans paramètre, c'est l'URL d'origine qui décide
 * (/my-analyses ou /playbook). Le lancement d'une analyse passe par le VRAI
 * tiroir Edgy (mêmes étapes, même verrou, même consommation de crédits).
 */
export default function LiteEdgyPage({ initialTab = "analyses" }: { initialTab?: Tab }) {
  const T = useTranslation();
  const t = T.lite.edgy;
  const { openUpgrade } = useLiteActions();
  const [params, setParams] = useSearchParams();
  const vue = params.get("vue");
  const tab: Tab = vue === "analyses" || vue === "playbook" ? vue : initialTab;

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Incrémenté à la fermeture du tiroir : l'analyse passe par un flux SSE, que
  // Wasp n'invalide pas, donc la liste doit être relue explicitement.
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: credits, refetch: refetchCredits } = useQuery(getEdgyCreditState, {});
  // Le verdict serveur (betaLocked) l'emporte dès qu'il est chargé, comme dans le tiroir.
  const { isLocked } = useEdgyAccess(credits);

  const selectTab = (id: Tab) => {
    const next = new URLSearchParams(params);
    next.delete("rapport");
    if (id === initialTab) next.delete("vue"); else next.set("vue", id);
    setParams(next);
  };

  // Plan verrouillé : directement la vraie modale de plans, sans analyse « découverte ».
  const launch = () => (isLocked ? openUpgrade() : setDrawerOpen(true));

  return (
    <>
      <PageHeader
        title={t.title}
        subtitle={t.subtitle}
        filters={false}
        actions={
          <Button
            onClick={launch}
            className="shrink-0"
            aria-label={t.newAnalysis}
            title={isLocked ? T.edgy.betaLock.lockedTooltip : undefined}
          >
            {isLocked ? <Lock className="h-4 w-4 sm:mr-1.5" /> : <Sparkles className="h-4 w-4 sm:mr-1.5" />}
            {/* Mobile : icône seule, le titre de page garde la largeur. */}
            <span className="hidden sm:inline">{t.newAnalysis}</span>
          </Button>
        }
      />
      <PageBody>
        <Segmented
          value={tab}
          onChange={selectTab}
          options={[
            { id: "analyses", label: t.tabs.analyses },
            { id: "playbook", label: t.tabs.playbook },
          ]}
        />

        {tab === "analyses" ? (
          <AnalysesTab
            credits={credits ?? null}
            isLocked={isLocked}
            refreshKey={refreshKey}
            onLaunch={launch}
            onUpgrade={openUpgrade}
          />
        ) : (
          <PlaybookTab isLocked={isLocked} onUpgrade={openUpgrade} />
        )}
      </PageBody>

      <EdgyDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setRefreshKey(k => k + 1);
          void refetchCredits();
        }}
        // Radix n'empile pas bien deux Dialog : on ferme le tiroir avant d'ouvrir les plans.
        onUpgrade={() => {
          setDrawerOpen(false);
          setTimeout(openUpgrade, 0);
        }}
      />
    </>
  );
}
